/**
 * "Trade Verification" for one stored TEK_RSI_MOMENTUM backtest run: every
 * trade in a sortable/filterable table, a verification panel + the Trade
 * Review chart for the selected trade, manual status/note, CSV exports.
 * Read-only except the manual annotation; nothing here changes the run.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, ButtonGroup, Chip, CircularProgress, FormControlLabel, MenuItem, Stack, Switch, Table, TableBody, TableCell,
  TableHead, TablePagination, TableRow, TableSortLabel, TextField, Typography,
} from "@mui/material";
import rsiBacktestVerificationService from "@/services/rsiBacktestVerificationService";
import type { RsiBacktestTradeDetail, RsiBacktestTradeFilters, RsiBacktestTradeList, RsiTradeReview } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import TradeReviewCharts from "../rsi-trade-review/TradeReviewCharts";
import { DEFAULT_TOGGLES, focusWindow, inr, type Toggles, type Window } from "../rsi-trade-review/tradeReviewChartData";
import { fmt2, istText, signed } from "@/utils/istTime";
import TradeVerificationPanel from "./TradeVerificationPanel";
import { COLUMNS, STATUSES } from "./verificationConstants";

const EXIT_REASONS = ["TARGET_HIT", "STOP_LOSS_HIT", "BREAKEVEN_STOP_HIT", "TRAILING_STOP_HIT", "OPPOSITE_SIGNAL", "EOD_SQUARE_OFF"];
const STATUS_COLOR: Record<string, "default" | "success" | "error" | "warning" | "info"> = {
  UNVERIFIED: "default", VERIFIED_CORRECT: "success", VERIFIED_INCORRECT: "error", NEEDS_REVIEW: "warning", IGNORED: "info",
};
const SHORT_REASON: Record<string, string> = {
  TARGET_HIT: "TARGET", STOP_LOSS_HIT: "STOP", BREAKEVEN_STOP_HIT: "BE STOP", TRAILING_STOP_HIT: "TRAIL STOP",
  OPPOSITE_SIGNAL: "OPPOSITE", EOD_SQUARE_OFF: "EOD",
};
const cell = { whiteSpace: "nowrap", px: 0.75, py: 0.5, fontSize: 13 } as const;
const rText = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${signed(fmt2(v))}R`);

export default function BacktestTradeVerification({ backtestId }: { backtestId: string }) {
  const [filters, setFilters] = useState<RsiBacktestTradeFilters>({ sort_by: "trade_no", sort_dir: "asc", page: 1, page_size: 25 });
  const [list, setList] = useState<RsiBacktestTradeList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [detail, setDetail] = useState<RsiBacktestTradeDetail | null>(null);
  const [chart, setChart] = useState<RsiTradeReview | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [toggles, setToggles] = useState<Toggles>(DEFAULT_TOGGLES);
  const [win, setWin] = useState<Window>({ start: 0, end: 0 });
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setList(await rsiBacktestVerificationService.listTrades(backtestId, filters));
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  }, [backtestId, filters]);
  useEffect(() => { void load(); }, [load]);

  const openTrade = useCallback(async (no: number) => {
    setSelected(no);
    setLoadingDetail(true);
    setError(null);
    try {
      const [d, c] = await Promise.all([
        rsiBacktestVerificationService.getTrade(backtestId, no),
        rsiBacktestVerificationService.getTradeCandles(backtestId, no),
      ]);
      setDetail(d);
      setChart(c);
      setWin(focusWindow(c));
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoadingDetail(false);
    }
  }, [backtestId]);

  const setF = (k: keyof RsiBacktestTradeFilters) => (e: { target: { value: string } }) =>
    setFilters((f) => ({ ...f, [k]: e.target.value || undefined, page: 1 }));
  const sort = (key: string) =>
    setFilters((f) => ({ ...f, sort_by: key, sort_dir: f.sort_by === key && f.sort_dir === "asc" ? "desc" : "asc", page: 1 }));
  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    try { await fn(); } catch (e) { setError(extractErrorMessage(e)); } finally { setBusy(null); }
  };
  const unfiltered: RsiBacktestTradeFilters = {};
  const toggle = (k: keyof Toggles, label: string) => (
    <FormControlLabel control={<Switch size="small" checked={toggles[k]} onChange={(e) => setToggles((t) => ({ ...t, [k]: e.target.checked }))} />} label={label} />
  );

  return (
    <Box data-testid="trade-verification">
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 1 }}>
        <TextField label="From" type="date" size="small" value={filters.date_from ?? ""} onChange={setF("date_from")} InputLabelProps={{ shrink: true }} />
        <TextField label="To" type="date" size="small" value={filters.date_to ?? ""} onChange={setF("date_to")} InputLabelProps={{ shrink: true }} />
        <TextField select label="Side" size="small" value={filters.side ?? ""} onChange={setF("side")} sx={{ minWidth: 100 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="LONG">Long</MenuItem><MenuItem value="SHORT">Short</MenuItem>
        </TextField>
        <TextField select label="Result" size="small" value={filters.outcome ?? ""} onChange={setF("outcome")} sx={{ minWidth: 110 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="PROFIT">Profit</MenuItem><MenuItem value="LOSS">Loss</MenuItem>
        </TextField>
        <TextField select label="Exit reason" size="small" value={filters.exit_reason ?? ""} onChange={setF("exit_reason")} sx={{ minWidth: 170 }}>
          <MenuItem value="">All</MenuItem>{EXIT_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </TextField>
        <TextField select label="Verification" size="small" value={filters.verification_status ?? ""} onChange={setF("verification_status")} sx={{ minWidth: 190 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="UNVERIFIED">UNVERIFIED</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField select label="Data quality" size="small" value={filters.data_quality ?? ""} onChange={setF("data_quality")} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="WARNING">With data warnings</MenuItem><MenuItem value="CLEAN">Clean data</MenuItem>
        </TextField>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">Trades CSV:</Typography>
        <ButtonGroup size="small" variant="outlined" disabled={busy !== null}>
          <Button disabled={selected === null} onClick={() => run("t1", () => rsiBacktestVerificationService.exportTrades(backtestId, { trade_nos: String(selected) }, `trade-${selected}`))}>Selected</Button>
          <Button onClick={() => run("tf", () => rsiBacktestVerificationService.exportTrades(backtestId, filters, "filtered"))}>Filtered</Button>
          <Button onClick={() => run("ta", () => rsiBacktestVerificationService.exportTrades(backtestId, unfiltered, "all"))}>All</Button>
        </ButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>Candles CSV:</Typography>
        <ButtonGroup size="small" variant="outlined" disabled={busy !== null}>
          <Button disabled={selected === null} onClick={() => run("c1", () => rsiBacktestVerificationService.exportTradeCandles(backtestId, selected as number))}>Selected</Button>
          <Button onClick={() => run("cf", () => rsiBacktestVerificationService.exportCandles(backtestId, filters, "filtered"))}>Filtered</Button>
          <Button onClick={() => run("ca", () => rsiBacktestVerificationService.exportCandles(backtestId, unfiltered, "all"))}>All</Button>
        </ButtonGroup>
        {busy && <CircularProgress size={16} />}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1 }} data-testid="verification-error">{error}</Alert>}
      {list?.data_quality && list.data_quality.missing_sessions.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid="missing-sessions">
          MISSING_SESSION_DATA — no candle data for trading day(s) {list.data_quality.missing_sessions.join(", ")}.
          {Object.keys(list.data_quality.sessions_after_gap).length > 0 &&
            ` Indicators on ${Object.keys(list.data_quality.sessions_after_gap).join(", ")} carry over across the gap; trades there are flagged.`}
        </Alert>
      )}
      {!list ? (
        <CircularProgress size={22} />
      ) : list.total === 0 ? (
        <Typography color="text.secondary">No trades match these filters.</Typography>
      ) : (
        <>
          <Box sx={{ overflowX: "auto", maxHeight: 360 }}>
            <Table size="small" stickyHeader data-testid="bt-trade-table">
              <TableHead>
                <TableRow>
                  {COLUMNS.map(([label, key]) => (
                    <TableCell key={label} sx={cell} data-testid={`col-${label}`}>
                      {key ? (
                        <TableSortLabel active={filters.sort_by === key} direction={filters.sort_by === key ? filters.sort_dir : "asc"} onClick={() => sort(key)}>{label}</TableSortLabel>
                      ) : label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {list.trades.map((t) => (
                  <TableRow key={t.trade_no} hover selected={t.trade_no === selected} sx={{ cursor: "pointer" }} onClick={() => void openTrade(t.trade_no)} data-testid={`bt-trade-${t.trade_no}`}>
                    <TableCell sx={cell}>{t.trade_no}</TableCell>
                    <TableCell sx={cell}><Chip size="small" variant="outlined" label={t.direction} color={t.direction === "LONG" ? "success" : "error"} /></TableCell>
                    <TableCell sx={cell}>{istText(t.entry_time_ist)}</TableCell>
                    <TableCell sx={cell}>{fmt2(t.entry_price)}</TableCell>
                    <TableCell sx={cell}>{fmt2(t.stop_loss)}</TableCell>
                    <TableCell sx={cell}>{fmt2(t.target)}</TableCell>
                    <TableCell sx={cell}>{t.exit_time_ist ? istText(t.exit_time_ist) : "—"}</TableCell>
                    <TableCell sx={cell}>{fmt2(t.exit_price)}</TableCell>
                    <TableCell sx={cell} title={t.exit_reason ?? ""}>{t.exit_reason ? SHORT_REASON[t.exit_reason] ?? t.exit_reason : "—"}</TableCell>
                    <TableCell sx={{ ...cell, color: (t.net_pnl ?? 0) >= 0 ? "success.main" : "error.main", fontWeight: 600 }} data-testid={`net-${t.trade_no}`}>{signed(inr(t.net_pnl))}</TableCell>
                    <TableCell sx={{ ...cell, fontWeight: 600 }} data-testid={`r-${t.trade_no}`}
                      title={t.partial_qty ? `partial ${rText(t.partial_r_multiple)} + final ${rText(t.final_exit_r_multiple)}` : undefined}>{rText(t.r_multiple)}</TableCell>
                    <TableCell sx={cell}><Chip size="small" label={t.verification_status} color={STATUS_COLOR[t.verification_status]} /></TableCell>
                    <TableCell sx={cell}>
                      {t.data_quality_flags && t.data_quality_flags.length > 0
                        ? <Chip size="small" color="warning" variant="outlined" label={t.data_quality_flags.join(", ")} data-testid={`dq-${t.trade_no}`} />
                        : "OK"}
                    </TableCell>
                    <TableCell sx={cell}>{signed(inr(t.gross_pnl))}</TableCell>
                    <TableCell sx={cell}>{inr(t.costs)}</TableCell>
                    <TableCell sx={cell}>{t.partial_qty ? `${t.partial_qty} @ ${fmt2(t.partial_price)}` : t.partial_time_ist ? "1st tgt hit · 0 booked" : "—"}</TableCell>
                    <TableCell sx={cell}>{fmt2(t.holding_minutes)}</TableCell>
                    <TableCell sx={cell}>{fmt2(t.mfe_points)}</TableCell>
                    <TableCell sx={cell}>{fmt2(t.mae_points)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            component="div" count={list.total} page={(filters.page ?? 1) - 1} rowsPerPage={filters.page_size ?? 25}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, p) => setFilters((f) => ({ ...f, page: p + 1 }))}
            onRowsPerPageChange={(e) => setFilters((f) => ({ ...f, page_size: Number(e.target.value), page: 1 }))}
          />
          <Typography variant="caption" color="text.secondary" data-testid="model-limitation-note">
            Model limitation (not a data problem): OPTION_PREMIUM_NOT_SIMULATED — P&L = NIFTY points × delta × quantity; no option contract is simulated.
            R = total realized P&L (partial + final exit) ÷ initial risk. Times are IST; values shown to 2 decimals.
          </Typography>
        </>
      )}

      {loadingDetail && <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress /></Box>}
      {!loadingDetail && detail && chart && selected !== null && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>NIFTY 5-minute · {chart.summary.trade_date} · trade #{selected}</Typography>
            <Box sx={{ flex: 1 }} />
            {toggle("vwap", "VWAP")}{toggle("ema20", "EMA(20)")}{toggle("levels", "Stop / target")}{toggle("rawCrosses", "Raw crosses")}{toggle("rsi", "RSI panel")}
          </Stack>
          <TradeReviewCharts review={chart} toggles={toggles} window={win} onWindow={setWin} />
          <Box sx={{ mt: 2 }}>
            <TradeVerificationPanel key={`${selected}-${detail.trade.verification_updated_at ?? ""}`} backtestId={backtestId} detail={detail}
              onSaved={() => { void load(); void openTrade(selected); }} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
